/**
 * Events empty-state copy + resolver (#3059)
 *
 * One source of truth for the events surface's empty-state copy and the
 * role/state -> copy mapping, shared by the Activity events tab (EventsTab)
 * and the Home overview sibling (EmptyEventsFallback). The native port (#3064)
 * mirrors these strings, so keep them here as greppable constants rather than
 * inlined at each call site.
 *
 * Design decisions are pinned in the #3059 PR body. The load-bearing rule:
 * never surface a primary CTA the API will refuse. "Create Event" is a
 * leader/owner capability (backend `routes/events.ts` create gate:
 * `clubRole !== 'leader' && clubRole !== 'owner'` -> forbidden), so it renders
 * only when the viewer leads or owns at least one club.
 */

export type EventsEmptyPrimaryAction = 'create-event' | 'find-club';
export type EventsEmptySecondaryAction = 'see-past-events';

export interface EventsEmptyStateDescriptor {
  title: string;
  message: string;
  primary?: { label: string; action: EventsEmptyPrimaryAction };
  secondary?: { label: string; action: EventsEmptySecondaryAction };
}

export interface EventsEmptyStateInput {
  /** Viewer leads or owns >= 1 club (the exact backend create-event gate). */
  isLeaderOfAnyClub: boolean;
  /** Viewer is a member of >= 1 club. */
  hasClubs: boolean;
  /** Any past events exist for the surface's scope. */
  hasPastEvents: boolean;
  /**
   * Whether the surface can switch to a past-events view in place. The Activity
   * events tab has the "Show:" filter (true); the Home overview does not (false),
   * so it never offers a "See past events" secondary that would dead-end.
   */
  supportsPastFilter: boolean;
}

export const EVENTS_EMPTY_COPY = {
  // Shared "no upcoming" title across roles/states (sentence case per the
  // majority EmptyState convention).
  noUpcomingTitle: 'No upcoming events yet',

  // Member (cannot create), past events exist -> calm, points at the secondary.
  memberPastMessage: 'Nothing coming up right now.',

  // Member (cannot create), no events at all -> calm, honest, no forward move.
  memberNoneMessage: "Your club hasn't scheduled anything yet.",

  // Leader/owner, past events exist -> the forward move is scheduling the next.
  leaderPastMessage: 'Schedule the next one so members can RSVP.',

  // Leader/owner, no events at all.
  leaderNoneMessage: 'Schedule your first event so members can RSVP.',

  // No clubs -> the productive path is finding a club, not creating an event.
  noClubsTitle: 'Find a club to get moving',
  noClubsMessage: 'Join a club to see its events here.',

  // Past filter empty (unchanged behavior, kept here for one source of truth).
  pastEmptyTitle: 'No past events',
  pastEmptyMessage: "Events you've attended will appear here",

  // Action labels.
  createEventLabel: 'Create Event',
  findClubLabel: 'Find a club',
  seePastEventsLabel: 'See past events',
} as const;

/**
 * Resolve the diagnosis-accurate empty state for "no upcoming events" (filter
 * upcoming/all). The past-filter empty state is a separate, action-free case
 * handled at the call site.
 */
export function resolveEventsEmptyState(
  input: EventsEmptyStateInput,
): EventsEmptyStateDescriptor {
  const { isLeaderOfAnyClub, hasClubs, hasPastEvents, supportsPastFilter } = input;

  // No clubs: the only productive move is finding a club. Never Create Event.
  if (!hasClubs) {
    return {
      title: EVENTS_EMPTY_COPY.noClubsTitle,
      message: EVENTS_EMPTY_COPY.noClubsMessage,
      primary: { label: EVENTS_EMPTY_COPY.findClubLabel, action: 'find-club' },
    };
  }

  const secondary: EventsEmptyStateDescriptor['secondary'] =
    supportsPastFilter && hasPastEvents
      ? { label: EVENTS_EMPTY_COPY.seePastEventsLabel, action: 'see-past-events' }
      : undefined;

  // Leader/owner of >= 1 club: Create Event is a real, non-refused move.
  if (isLeaderOfAnyClub) {
    return {
      title: EVENTS_EMPTY_COPY.noUpcomingTitle,
      message: hasPastEvents
        ? EVENTS_EMPTY_COPY.leaderPastMessage
        : EVENTS_EMPTY_COPY.leaderNoneMessage,
      primary: { label: EVENTS_EMPTY_COPY.createEventLabel, action: 'create-event' },
      secondary,
    };
  }

  // Plain member/moderator: calm, and never a Create Event the API would refuse.
  return {
    title: EVENTS_EMPTY_COPY.noUpcomingTitle,
    message: hasPastEvents
      ? EVENTS_EMPTY_COPY.memberPastMessage
      : EVENTS_EMPTY_COPY.memberNoneMessage,
    secondary,
  };
}
